/**
 * One law, as something you can read rather than decode.
 *
 * The plain-English title leads and the official one is demoted to a
 * footnote, because "Expands emergency disaster funding and advance payments
 * for farmers" tells you what happened and "Emergency Conservation Program
 * Improvement Act of 2025" does not.
 *
 * A law with no summary is still shown, with its official title and a note
 * saying so. Hiding it would make the catalogue look complete when it is
 * not, and the gap is worth seeing.
 */

import type { Law } from "../api";
import { Card, LawNumber, Pill } from "./bits";
import { when } from "../format";

export function LawCard({ law }: { law: Law }) {
  const plain = law.plain_title?.trim();
  const points = law.key_points ?? [];

  return (
    <Card to={`/law/${law.bill_id}`} className="group p-6">
      {/* Two fixed rows rather than one wrapping one. A subject heading can
          be "Armed Forces and National Security", and letting it share a
          line with the date means the date lands wherever the heading runs
          out -- so a grid of cards has it in a different place on each. */}
      <div className="flex items-baseline justify-between gap-3">
        <LawNumber value={law.law_number} />
        {law.latest_action_date && (
          <span className="shrink-0 text-xs text-faint">{when(law.latest_action_date)}</span>
        )}
      </div>
      {law.policy_area && (
        <div className="mt-2">
          <Pill>{law.policy_area}</Pill>
        </div>
      )}

      <h3 className="mt-3 font-display text-xl leading-snug font-semibold text-balance group-hover:text-brand">
        {plain || law.title}
      </h3>

      {plain && <p className="mt-1.5 text-sm text-faint">Officially: {law.title}</p>}

      {points.length > 0 ? (
        <ul className="mt-4 space-y-1.5">
          {points.slice(0, 2).map((point, n) => (
            <li key={n} className="flex gap-2.5 text-sm leading-relaxed text-muted">
              <span aria-hidden className="mt-2 size-1 shrink-0 rounded-full bg-brand" />
              <span className="line-clamp-2">{point}</span>
            </li>
          ))}
          {points.length > 2 && (
            <li className="pl-5 text-sm font-medium text-brand">
              and {points.length - 2} more {points.length - 2 === 1 ? "point" : "points"}
            </li>
          )}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-faint italic">
          Not yet summarised. The official text is still here.
        </p>
      )}

      {law.who_it_affects && (
        <p className="mt-4 border-t border-line pt-3 text-sm text-muted">
          <span className="font-medium text-ink">Who it affects: </span>
          <span className="line-clamp-2">{law.who_it_affects}</span>
        </p>
      )}
    </Card>
  );
}
